import React, { useEffect, useRef, lazy, Suspense } from "react";
import Typed from "typed.js";
import { motion, useSpring } from "framer-motion";
import {
  Box,
  Typography,
  Button,
  IconButton,
  useTheme,
  styled,
  CircularProgress,
} from "@mui/material";

import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";
import InstagramIcon from "@mui/icons-material/Instagram";
import CodeIcon from "@mui/icons-material/Code";
import sarthakImg from "../assets/sarthak.jpg";

// Lazy Components
const About = lazy(() => import("./About"));
const MyStory = lazy(() => import("./MyStory"));
const Skills = lazy(() => import("./Skills"));
const Resume = lazy(() => import("./Resume"));
const Projects = lazy(() => import("./Projects"));
const Contact = lazy(() => import("./Contact"));

const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
}));

const Notification = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[6],
  zIndex: 1500,
  opacity: 0,
  transition: "opacity 0.3s ease-in-out",
  display: "none",
  minWidth: 250,
}));

const ProgressBar = styled(Box)(({ theme }) => ({
  height: 4,
  backgroundColor: theme.palette.primary.main,
  width: "100%",
  marginTop: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
}));

const Home = () => {
  const typedRef = useRef(null);
  const theme = useTheme();
  const formRef = useRef(null);
  const notificationRef = useRef(null);
  const notificationTextRef = useRef(null);
  const progressBarRef = useRef(null);

  useEffect(() => {
    typedRef.current = new Typed(".multiple-text", {
      strings: [
        "MERN Stack Developer",
        "Full Stack Web Developer",
        "Competitive Programmer",
        "Backend Architecture Expert",
        "UI/UX Enthusiast",
      ],
      typeSpeed: 80,
      backDelay: 1200,
      backSpeed: 80,
      loop: true,
    });
    return () => {
      if (typedRef.current) {
        typedRef.current.destroy();
      }
    };
  }, []);

  const scrollProgress = useSpring(0, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = window.scrollY / totalHeight;
      scrollProgress.set(progress);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrollProgress]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const projectCardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.6,
        ease: "easeOut",
      },
    }),
  };

  const skillCardVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut",
      },
    }),
  };

  const showNotification = (message, type) => {
    const notification = notificationRef.current;
    const text = notificationTextRef.current;
    const progress = progressBarRef.current;
    if (!notification || !text || !progress) return;
    text.innerHTML = message;
    text.style.color = type === "success" ? theme.palette.primary.main : "#dc3545";
    notification.style.display = "block";
    notification.style.opacity = 1;
    progress.style.transition = "none";
    progress.style.width = "100%";
    setTimeout(() => {
      progress.style.transition = "width 5s linear";
      progress.style.width = "0%";
    }, 50);
    setTimeout(() => {
      notification.style.opacity = 0;
      setTimeout(() => {
        notification.style.display = "none";
        progress.style.transition = "none";
        progress.style.width = "100%";
      }, 300);
    }, 5000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simplified form handling for the demonstration
    // In a real app, you'd use emailjs here as previously implemented
    showNotification("Thank you! Your message has been sent.", "success");
    e.target.reset();
  };

  return (
    <Box sx={{ pt: { xs: 8, md: 0 }, position: 'relative' }}>
      <motion.div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #00E5FF 0%, #7C4DFF 100%)",
          zIndex: 2000,
          scaleX: scrollProgress,
          transformOrigin: "0%",
        }}
      />

      {/* Hero Section */}
      <Section id="Home" sx={{ position: "relative", overflow: "hidden" }}>
        {/* Background Decorations */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            left: "5%",
            width: "300px",
            height: "300px",
            background: "radial-gradient(circle, rgba(0, 229, 255, 0.15) 0%, rgba(0, 229, 255, 0) 70%)",
            filter: "blur(60px)",
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            right: "5%",
            width: "400px",
            height: "400px",
            background: "radial-gradient(circle, rgba(124, 77, 255, 0.15) 0%, rgba(124, 77, 255, 0) 70%)",
            filter: "blur(80px)",
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 6, md: 4 },
            maxWidth: "1200px",
            width: "90%",
            mx: "auto",
            zIndex: 1,
            textAlign: { xs: "center", md: "left" },
          }}
        >
          {/* Text Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ flex: 1.2 }}
          >
            <Box>
              <motion.div variants={itemVariants}>
                <Typography 
                  variant="h6" 
                  color="primary" 
                  sx={{ 
                    fontWeight: 600, 
                    letterSpacing: 3, 
                    mb: 2,
                    textTransform: "uppercase" 
                  }}
                >
                  Welcome to my portfolio
                </Typography>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Typography 
                  variant="h1" 
                  gutterBottom 
                  sx={{ 
                    fontSize: { xs: "3.5rem", md: "5rem" }, 
                    lineHeight: 1.1,
                    mb: 3
                  }}
                >
                  I'm <Typography 
                    component="span" 
                    variant="inherit" 
                    sx={{ 
                      background: "linear-gradient(135deg, #00E5FF 0%, #7C4DFF 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Sarthak Singh
                  </Typography>
                </Typography>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    fontSize: { xs: "1.5rem", md: "2.5rem" },
                    color: "text.secondary",
                    mb: 3
                  }}
                >
                  A Creative <Typography
                    variant="inherit"
                    fontWeight={700}
                    component="span"
                    className="multiple-text"
                    color="primary"
                  />
                </Typography>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: "1.1rem", 
                    color: "text.secondary",
                    maxWidth: "500px",
                    mx: { xs: "auto", md: "0" },
                    mb: 4,
                    lineHeight: 1.7
                  }}
                >
                  Crafting high-performance backends and seamless user experiences with a focus on scalability and clean architecture.
                </Typography>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Box sx={{ mb: 5, display: "flex", justifyContent: { xs: "center", md: "flex-start" }, gap: 2 }}>
                  {[
                    { icon: <LinkedInIcon />, url: "https://in.linkedin.com/in/sarthak03dot" },
                    { icon: <GitHubIcon />, url: "https://github.com/sarthak03dot" },
                    { icon: <InstagramIcon />, url: "https://www.instagram.com/sarthak03dot/" },
                    { icon: <CodeIcon />, url: "https://leetcode.com/u/sarthak03dot/" }
                  ].map((social, i) => (
                    <IconButton
                      key={i}
                      href={social.url}
                      target="_blank"
                      sx={{
                        color: "text.secondary",
                        border: "1px solid rgba(255,255,255,0.1)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          color: "primary.main",
                          borderColor: "primary.main",
                          transform: "translateY(-3px)",
                          backgroundColor: "rgba(0, 229, 255, 0.05)"
                        }
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Box>
              </motion.div>
              <motion.div variants={itemVariants} whileHover={{ scale: 1.05 }}>
                <Button
                  variant="contained"
                  href="./assets/FSD_SARTHAKSINGH.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="large"
                  sx={{ 
                    px: 5, 
                    py: 1.8, 
                    fontSize: "1.1rem",
                    boxShadow: "0 10px 20px rgba(0, 229, 255, 0.2)" 
                  }}
                >
                  Download CV
                </Button>
              </motion.div>
            </Box>
          </motion.div>

          {/* Profile Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            style={{ flex: 0.8, display: "flex", justifyContent: "center" }}
          >
            <Box
              sx={{
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 20,
                  left: 20,
                  right: -20,
                  bottom: -20,
                  borderRadius: 4,
                  border: "2px solid",
                  borderColor: "primary.main",
                  opacity: 0.5,
                  zIndex: -1,
                }
              }}
            >
              <Box
                sx={{
                  width: { xs: 240, md: 320 },
                  height: { xs: 240, md: 320 },
                  borderRadius: 4,
                  overflow: "hidden",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                  border: "2px solid rgba(255,255,255,0.1)",
                }}
              >
                <img
                  src={sarthakImg}
                  alt="Profile"
                  loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            </Box>
          </motion.div>
        </Box>
      </Section>

      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress color="primary" />
        </Box>
      }>
        <About containerVariants={containerVariants} itemVariants={itemVariants} />
        <MyStory containerVariants={containerVariants} itemVariants={itemVariants} />
        <Skills containerVariants={containerVariants} itemVariants={itemVariants} skillCardVariants={skillCardVariants} />
        <Resume containerVariants={containerVariants} itemVariants={itemVariants} />
        <Projects containerVariants={containerVariants} itemVariants={itemVariants} projectCardVariants={projectCardVariants} />
        <Contact 
          containerVariants={containerVariants} 
          itemVariants={itemVariants} 
          formRef={formRef}
          handleSubmit={handleSubmit}
        />
      </Suspense>

      <Notification ref={notificationRef}>
        <Typography ref={notificationTextRef}></Typography>
        <ProgressBar ref={progressBarRef} />
      </Notification>
    </Box>
  );
};

export default Home;
